import isEqual from "lodash/isEqual";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import FloatingButtons from "../../../../parts/FloatingButtons";
import TimeInput from "../../../../parts/inputs/time-input";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import OptionToggler from "../../../../parts/OptionToggler";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { useRillContext } from "../../../../rill/RillReact/share/base";

type MorningBriefOptions = {
  status?: string;
  sendTime?: string;
  allowClientOverride?: boolean;
};

type MorningBriefState = {
  sendTime: string;
  allowClientOverride: boolean;
};

const toState = (morningBrief: MorningBriefOptions): MorningBriefState => ({
  sendTime: morningBrief.sendTime ?? "",
  allowClientOverride: morningBrief.allowClientOverride === true,
});

// Morning Brief's on/off switch is its own `status` (draft/published) — the
// same field every getting-started option already uses, not a separate
// `enabled` boolean living alongside it. Fires immediately (with a
// confirmation), independent of the batched field save below.
const MorningBriefStatusToggle = ({
  workflow,
  status,
}: {
  workflow: string;
  status?: string;
}) => {
  const { t } = useTranslation("coaching-portal");
  const published = status === "published";
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);

  const handleChange = () => {
    submit(
      `/actions/coach-daily-monitoring/${published ? "unpublish" : "publish"}/${workflow}`,
      { method: "GET" }
    );
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 250);
  };

  return (
    <div className="option">
      <p className="p4">{t("customWorkflows.optionName.morningBrief")}</p>
      <div>
        <input
          type="checkbox"
          id="switch-morningBrief"
          name="switch-morningBrief"
          role="switch"
          aria-checked={published}
          onChange={() => setOpenedConfirmationModal((prev) => !prev)}
          checked={published}
        />
        <ConfirmationModal
          title={
            published
              ? t("customWorkflows.unpublish")
              : t("customWorkflows.publish")
          }
          contentText={
            published
              ? t("customWorkflows.confirmUnpublishOption")
              : t("customWorkflows.confirmPublishOption")
          }
          cancelButtonText={t("customWorkflows.cancel")}
          cancelButtonAction={() => setOpenedConfirmationModal((prev) => !prev)}
          confirmButtonText={
            published
              ? t("customWorkflows.unpublish")
              : t("customWorkflows.publish")
          }
          confirmButtonAction={handleChange}
          isOpen={openedConfirmationModal}
          confirmSuccess={!published}
        />
      </div>
    </div>
  );
};

/**
 * "Edit Workflow → Monitoring" screen. Built as a stacked container — Morning
 * Brief's block today, with room for Daily Reflection's block to be added
 * below it later (see msb-docs/morning-brief/morning-brief-implementation-plan.md Stage 4).
 */
export const CoachDailyMonitoring = () => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const { _id, type, options } = locals.workflow as {
    _id: string;
    type: string;
    options?: { morningBrief?: MorningBriefOptions };
  };
  const morningBrief = options?.morningBrief ?? {};

  const initialState = toState(morningBrief);
  const [initial, setInitial] = useState(initialState);
  const [state, setState] = useState(initialState);
  const canSubmit = !isEqual(initial, state);

  const allowOverrideOptions = [
    { label: t("common:yes"), value: true },
    { label: t("common:no"), value: false },
  ];

  const handleSave = () => {
    submit("/actions/coach-daily-monitoring/save", {
      method: "POST",
      body: { workflow: _id, changes: state },
    });
    setInitial(state);
  };

  const handleDiscard = () => {
    setState(initial);
  };

  return (
    <main className="coach-daily-monitoring">
      <header>
        <hgroup>
          <SvgIcon
            src="/img/new-icons/back-button.svg"
            useCurrentColor={undefined}
            style={undefined}
            onClickFn={() => {
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: { workflow: "", type: "" },
              });
            }}
          />
          <div>
            <h3>{t(`customWorkflows.workflowName.${type}`)}</h3>
          </div>
        </hgroup>
      </header>

      <div>
        <aside>
          <div className="flex justify-between items-center gap-2 mb-6">
            <h4>{t("customWorkflows.gettingStarted.listOfOptions")}</h4>
            <ToolTip
              id="options-tooltip"
              body={t("customWorkflows.gettingStarted.optionsTooltip")}
            >
              <SvgIcon
                alt={t("customWorkflows.gettingStarted.infoIconAlt")}
                src="/img/new-icons/question-circle.svg"
                useCurrentColor={undefined}
                style={undefined}
              />
            </ToolTip>
          </div>

          {/* Only Morning Brief today — Daily Reflection's row is added here,
              below it, once that phase starts. */}
          <MorningBriefStatusToggle
            workflow={_id}
            status={morningBrief.status}
          />
        </aside>
        <section>
          <article>
            <h4>{t("coachDailyMonitoring.morningBriefTitle")}</h4>
            <p className="p5 mt-2 mb-4">
              {t("coachDailyMonitoring.morningBriefDescription")}
            </p>

            <div className="row">
              <p>{t("coachDailyMonitoring.sendTime")}</p>
              <TimeInput
                name="morningBriefSendTime"
                value={state.sendTime}
                onChange={(value: string) =>
                  setState((prev) => ({ ...prev, sendTime: value }))
                }
              />
            </div>

            <div className="row">
              <p>{t("coachDailyMonitoring.allowClientOverride")}</p>
              <OptionToggler
                name="morningBriefAllowClientOverride"
                options={allowOverrideOptions}
                defaultValue={state.allowClientOverride}
                onSelectFn={(value: boolean) =>
                  setState((prev) => ({ ...prev, allowClientOverride: value }))
                }
              />
            </div>
          </article>
        </section>
      </div>

      <footer>
        <FloatingButtons
          failedText={t("common:discard")}
          successText={t("common:save")}
          successDisabled={!canSubmit}
          handleFailed={handleDiscard}
          handleSuccess={handleSave}
          classNames={undefined}
          label={undefined}
        />
      </footer>
    </main>
  );
};
